import styled, { css } from 'styled-components';
import { CircleCheckBig } from 'lucide-react';

export const FormContainer = styled.div`
  width: 100%;
`;

export const FormCard = styled.div`
  background-color: white;
  border-radius: 0.5rem;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06);
  overflow: hidden;
`;

export const CardHeader = styled.div`
  padding: 1.5rem;
  border-bottom: 1px solid #e5e7eb;
`;

export const CardTitle = styled.h2`
  font-size: 1.25rem;
  font-weight: 600;
  color: #111827;
  margin-bottom: 0.5rem;
`;

export const CardDescription = styled.p`
  font-size: 0.875rem;
  color: #6b7280;
`;

export const CardContent = styled.div`
  padding: 1.5rem;
`;

export const FormGroup = styled.div`
  margin-bottom: 1.5rem;
`;

export const RecaptchaGroup = styled.div`
  margin-bottom: 1rem;
  display: flex;
  align-items: center;
  justify-content: center;
`;

export const Label = styled.label`
  display: block;
  font-size: 0.875rem;
  font-weight: 500;
  color: #374151;
  margin-bottom: 0.5rem;
`;
export const RedAsterisk = styled.span`
  display: inline-block;
  font-size: 1rem;
  font-weight: 500;
  color: rgb(219, 72, 18);
`;

export const Input = styled.input`
  width: 100%;
  padding: 0.5rem 0.75rem;
  font-size: 0.875rem;
  line-height: 1.25rem;
  color: #111827;
  background-color: white;
  border: 1px solid #d1d5db;
  border-radius: 0.375rem;
  box-shadow: 0 1px 2px 0 rgba(0, 0, 0, 0.05);
  transition: border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out;

  &:focus {
    outline: none;
    border-color: #3b82f6;
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
  }

  &::placeholder {
    color: #9ca3af;
  }
`;

export const TextArea = styled.textarea`
  width: 100%;
  min-height: 120px;
  padding: 0.5rem 0.75rem;
  font-size: 0.875rem;
  line-height: 1.25rem;
  color: #111827;
  background-color: white;
  border: 1px solid #d1d5db;
  border-radius: 0.375rem;
  box-shadow: 0 1px 2px 0 rgba(0, 0, 0, 0.05);
  transition: border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out;
  resize: vertical;

  &:focus {
    outline: none;
    border-color: #3b82f6;
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
  }

  &::placeholder {
    color: #9ca3af;
  }
`;

export const DropZone = styled.div`
  border: 2px dashed #d1d5db;
  border-radius: 0.375rem;
  padding: 1.5rem;
  text-align: center;
  transition: all 0.2s ease;

  ${(props) =>
    props.isDragging &&
    css`
      border-color: #3b82f6;
      background-color: rgba(59, 130, 246, 0.05);
    `}

  .hidden-input {
    display: none;
  }

  .dropzone-text {
    font-size: 0.875rem;
    font-weight: 500;
    margin-top: 1rem;
    margin-bottom: 0.25rem;
  }

  .dropzone-subtext {
    font-size: 0.75rem;
    color: #6b7280;
  }

  .file-header {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 0.5rem;
  }

  .file-name {
    font-weight: 500;
  }

  .file-status {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 0.25rem;
    margin-top: 0.5rem;
  }

  .success-icon {
    color: #10b981;
  }

  .success-text {
    font-size: 0.875rem;
    color: #10b981;
  }
`;

export const DropZoneContent = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1rem;
`;

export const FilePreview = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.5rem;
`;

export const FileInfo = styled.p`
  font-size: 0.75rem;
  color: #6b7280;
`;

export const SuccessIcon = styled(CircleCheckBig)`
  color: #10b981;
`;

export const RemoveButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  color: #6b7280;
  padding: 0.25rem;
  border-radius: 9999px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background-color 0.2s ease;

  &:hover {
    background-color: #f3f4f6;
  }
`;

export const ClearButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  color: #6b7280;
  padding: 0.25rem;
  border-radius: 9999px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background-color 0.2s ease;
  background-color: #f3f4f6;
`;

export const Button = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0.5rem 1rem;
  font-size: 0.875rem;
  font-weight: 500;
  border-radius: 0.375rem;
  transition: all 0.2s ease;
  cursor: pointer;

  ${(props) =>
    props.variant === 'primary' &&
    css`
      background-color: #cc3300;
      color: white;
      border: 1px solid transparent;

      &:hover:not(:disabled) {
        background-color: #a32800;
      }
    `}

  ${(props) =>
    props.variant === 'outline' &&
    css`
      background-color: transparent;
      color: #374151;
      border: 1px solid #d1d5db;

      &:hover:not(:disabled) {
        background-color: #f3f4f6;
      }
    `}

  ${(props) =>
    props.fullWidth &&
    css`
      width: 100%;
    `}

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

export const PositionContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  padding-left: 2rem;
  padding-top: 0.5rem;
  padding-bottom: 0.5rem;
`;

export const RowContainer = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 0.25rem;
  cursor: pointer;
`;

export const PlatformLabel = styled.label`
  display: flex;
  font-size: 0.8rem;
  font-weight: 500;
  color: #374151;
`;

export const Spacer = styled.div`
  height: ${(props) => props.height || '1rem'};
`;

export const ErrorText = styled.p`
  font-size: 0.85rem;
  color: #ef4444;
`;

export const ErrorContainer = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 0.25rem;
  margin-top: 0.25rem;
  margin-bottom: 0.75rem;

  @media (min-width: 640px) {
    display: none;
  }
`;

export const SuccessText = styled.p`
  font-size: 1rem;
  color: #10b981;
`;

export const SuccessContainer = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;
  gap: 0.25rem;
  margin-bottom: 0.75rem;

  @media (min-width: 640px) {
    display: none;
  }
`;
